import React from 'react';
import * as Yup from 'yup';
import {
  ApplicationAction,
  ApplicationActionType,
  Button,
  InputField,
  Modal,
  stripWhitespace,
} from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import { Field, Form, Formik, FormikTouched } from 'formik';
import { MiniAppFocusWrapper } from 'src/components';
import { serialNumberMask, serialNumberPattern } from 'src/helpers/formatting';
import { useApplication } from 'src/hooks/useApplication';
import { getProduct } from 'src/features/device-activation/helpers/utils';

interface Props {
  visible: boolean;
  onClose: () => void;
  showDuplicateModal?: (isDuplicate: boolean) => void;
  onContinue?: () => void;
  onVisible?: () => void;
  onError?: () => void;
}

interface InitValues {
  serialNumber: string;
  confirmSerial: string;
}

export function EnterSerialModal(props: Props) {
  const { application, dispatch, catalogue } = useApplication();
  const initialValues: InitValues = {
    serialNumber: '',
    confirmSerial: '',
  };

  React.useEffect(() => {
    if (props.onVisible && props.visible) {
      props.onVisible();
    }
  });

  const submit = (values: InitValues) => {
    const product = getProduct(catalogue.products, values.serialNumber.toUpperCase());
    const isDuplicate = application.cardMachines?.find((cardMachine) =>
      cardMachine.barcode.toLowerCase().includes(values.serialNumber.toLowerCase()),
    );
    if (props.showDuplicateModal) {
      props.showDuplicateModal(!!isDuplicate);
    }

    const actions: ApplicationAction[] = isDuplicate
      ? []
      : [
          {
            type: ApplicationActionType.UPDATE_CARD_MACHINE,
            cardMachines: [
              ...(application.cardMachines || []),
              {
                barcode: values.serialNumber.toUpperCase(),
                name: '',
                productId: product.device.id,
                acquiringChannel: product.device.acquiringChannel,
              },
            ],
          },
          { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP },
        ];

    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions,
    });
    if (props.onContinue) {
      props.onContinue();
    }
  };

  const sendErrorEvent = (isValid: boolean, touched: FormikTouched<InitValues>) => {
    if (!isValid && props.onError && Object.values(touched).length > 1) {
      props.onError();
    }
  };

  const isDuplicate = (serialNumber: string) =>
    application.cardMachines?.some((cardMachine) =>
      cardMachine.barcode.toLowerCase().includes(serialNumber.toLowerCase()),
    );

  return (
    <Modal onClose={props.onClose} visible={props.visible} size="small">
      <Formik
        initialValues={initialValues}
        validateOnBlur
        validateOnChange
        onSubmit={submit}
        validateOnMount
        validationSchema={Yup.object().shape({
          serialNumber: Yup.string()
            .required('Please enter serial number')
            .transform((originalValue) =>
              originalValue ? stripWhitespace(originalValue.toUpperCase()) : '',
            )
            .test(
              'is-not-duplicate',
              'Serial number has already been scanned',
              (serialNumber) => !isDuplicate(serialNumber),
            )
            .matches(serialNumberPattern, 'Invalid serial number format'),
          confirmSerial: Yup.string()
            .required('Please confirm your serial number')
            .transform((originalValue) =>
              originalValue ? stripWhitespace(originalValue.toUpperCase()) : '',
            )
            .oneOf([Yup.ref('serialNumber')], 'Serial number Fields must match'),
        })}
      >
        {({ isValid, touched }) => (
          <Form>
            {sendErrorEvent(isValid, touched)}
            <section className="flex flex-col items-center gap-7">
              <h1 className="text-4xl font-medium">Enter barcode</h1>
              <h3 className="text-lg font-normal">Please enter the barcode below</h3>
              <section className="flex flex-col gap-5 md:min-w-[370px]">
                <Field
                  name={'serialNumber'}
                  type="text"
                  mask={serialNumberMask}
                  id={`serialNumber`}
                  placeholder="Enter barcode"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  className="w-4/5 uppercase"
                  automationid="DA_Enter_Barcode"
                />

                <Field
                  name={'confirmSerial'}
                  type="text"
                  id={`confirmSerial`}
                  mask={serialNumberMask}
                  placeholder="Confirm barcode"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  className="w-4/5 uppercase"
                  automationid="DA_Confirm_Barcode"
                />
              </section>
              <section className="flex flex-col items-center w-4/5 gap-7">
                <Button
                  type={'submit'}
                  disabled={!isValid}
                  buttonStyle={DefaultButtonStyle()
                    .hasPrimaryAction()
                    .isMediumSize()
                    .hasLightMode()
                    .build()}
                  buttonParams={{
                    type: ButtonParamType.NormalButton,
                    onClick: () => {},
                  }}
                  className="w-56"
                  automationid={`DA_Continue_Button`}
                >
                  Continue
                </Button>
              </section>
            </section>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
